import _ from 'lodash'
import { Input } from '../input'
import { Button } from '../button'
import { KeyCode } from '../../core/KConstants'
import KPressOnTrigger from '../../core/utils/KPressOnTrigger'

export default {
  name: 'KInputNumber',
  mixins: [Input],
  props: {
    type: {
      type: String,
      default: '',
    },
    min: {
      type: Number,
      default: -Infinity,
    },
    max: {
      type: Number,
      default: Infinity,
    },
    loop: {
      type: Boolean,
      default: false,
    },
    value: {
      type: Number,
      default: 0,
    },
    step: { // 步进
      type: Number,
      default: 1,
    },
    precision: { // 精度，正整数
      type: Number,
      validator: value => value >= 0 && value === _.parseInt(value, 10),
    },
    size: {
      validator: value => ['large', 'normal', 'small', 'mini'].indexOf(value) !== -1,
    },
    formatter: {
      type: Function,
    },
    parser: {
      type: Function,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    hideButton: {
      type: Boolean,
      default: false,
    },
    hideInput: {
      type: Boolean,
      default: false,
    },
    buttonPosition: {
      validator: value => ['right'].indexOf(value) !== -1,
    },
    showButtonOnHover: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      innerValue: 0,
      inputValue: 0,
      intervalHandler: null,
      plusButtonType: 'light',
      minusButtonType: 'light',
      // 轮子事件上一次的时间戳
      preWheel: new Date().getTime(),
    }
  },
  computed: {
    calcPrecision() {
      // 当 step 为小数时，要计算对应的精度，否则可能造成 0.1 + 0.2 = 0.30000000000000004 的问题
      const stepPrecision = this.getPrecision(this.step)
      if (_.isNil(this.precision)) {
        return _.max([this.getPrecision(this.value), stepPrecision])
      }

      return this.precision
    },
    plusDisabled() {
      return !this.loop && (this.innerValue >= this.max || this.disabled)
    },
    minusDisabled() {
      return !this.loop && (this.innerValue <= this.min || this.disabled)
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(value) {
        this.setInnerValue(value)
      },
    },
    innerValue: {
      immediate: true,
      handler(value) {
        this.$emit('input', +value)
      },
    },
  },
  methods: {
    // 轮子事件改变数字的大小
    handleWheel(event) {
      event.preventDefault()
      // 当前轮子触发的时间戳
      const nowWheel = new Date().getTime()
      if ((event.deltaY === 1 || event.deltaY === -1) && ((nowWheel - this.preWheel) / 1000) < 0.3) {
        // 如果是触控板慢速操作 1或-1 且操作时间在300毫秒内 则不执行
        return
      }
      this.preWheel = nowWheel
      // 判断轮子事件的方向，模拟加减按钮
      if (event.deltaY > 0) {
        this.plus()
      } else {
        this.minus()
      }
    },
    getPrecision(value) {
      const valueString = '' + value
      const dotPosition = valueString.indexOf('.')
      return dotPosition !== -1 ? valueString.length - dotPosition - 1 : 0
    },
    plus() {
      if (this.plusDisabled) {
        return
      }
      this.setInnerValue(+this.innerValue + this.step)
    },
    minus() {
      if (this.minusDisabled) {
        return
      }
      this.setInnerValue(+this.innerValue - this.step)
    },
    setInnerValue(value) {
      value = +value
      if (isNaN(value)) {
        value = this.value // Math.max(0, this.min)
      }

      if (value > this.max) {
        if (this.loop) {
          value = this.min
        } else {
          value = this.max
        }
      } else if (value < this.min) {
        if (this.loop) {
          value = this.max
        } else {
          value = this.min
        }
      }

      if (this.calcPrecision) {
        value = +value.toFixed(this.calcPrecision)
      }

      this.innerValue = value
      // innerValue may not changed by different input value
      this.$nextTick(() => {
        this.setInputValue(value)
      })
    },
    setInputValue(value) {
      if (this.formatter && _.isFunction(this.formatter)) {
        this.inputValue = this.formatter(value)
      } else {
        this.inputValue = value
      }
    },
    handleInputChange(value) {
      this.inputValue = value // make sure input props: value changed

      if (this.parser && _.isFunction(this.parser)) {
        value = this.parser('' + value)
      }
      this.setInnerValue(value)
    },
    handleInputKeydown(e) {
      const keyCode = e.which
      if (keyCode !== KeyCode.UP && keyCode !== KeyCode.DOWN) {
        return
      }

      if (keyCode === KeyCode.UP) {
        this.plus()
      } else {
        this.minus()
      }
    },
    renderButton(plus = false) {
      if (this.hideButton) {
        return
      }

      const icon = plus ? 'plus' : 'minus'
      const slotName = plus ? 'suffix' : 'prefix'

      const css = {
        'km-input-number__button': true,
        [`km-input-number__button--${icon}`]: true,
      }

      const props = {
        disabled: this[`${icon}Disabled`],
        type: this[`${icon}ButtonType`],
      }

      const events = {
        ...KPressOnTrigger(this[icon]),
      }

      /**
       * 修复元素disabled时，document接受不到mouseup事件
       * https://stackoverflow.com/questions/62081666/the-event-of-the-document-is-not-triggered-when-it-is-on-a-disabled-element
       */
      const style = {
        'pointer-events': this[`${icon}Disabled`] ? 'none' : 'auto',
      }

      const nativeEvents = {
        mouseenter: () => {
          this[`${icon}ButtonType`] = this.type || 'primary'
        },
        mouseleave: () => {
          this[`${icon}ButtonType`] = 'light'
        },
      }

      const data = {
        class: css,
        props,
        on: events,
        style,
      }

      if (plus && this.$slots.plus) {
        return (
          <div
            {...data}
            slot={slotName}
          >
            {this.$slots.plus}
          </div>
        )
      } else if (this.$slots.minus) {
        return (
          <div
            {...data}
            slot={slotName}
          >
            {this.$slots.minus}
          </div>
        )
      }

      _.assign(data, { nativeOn: nativeEvents })
      return (
        <Button
          {...data}
          plainPlus={true}
          icon={this.buttonPosition === 'right' ? (plus ? 'angle-up' : 'angle-down') : icon}
          square={this.buttonPosition === 'right'}
          slot={slotName}
        >
        </Button>
      )
    },
    renderButtons() {
      return (
        <div
          class="km-input-number__buttons"
          slot="suffix"
        >
          <div>
            {this.renderButton(true)}
          </div>
          <div>
            {this.renderButton()}
          </div>
        </div>
      )
    },
  },
  render() {
    const style = {}

    const css = {
      'km-input-number': true,
      'km-input-number--disabled': this.disabled,
      'km-input-number--no-button': this.hideButton,
      'km-input-number--no-input': this.hideInput,
      'km-input-number--button-in-right': this.buttonPosition === 'right',
      'km-input-number--show-button-on-hover': this.showButtonOnHover,
    }
    if (this.size) {
      // css[`km-input-number--${this.size}`] = true
    }

    const attrs = {
      disabled: this.disabled,
    }

    const props = {
      value: this.inputValue,
      lazy: true,
      size: this.size,
    }

    const events = {
      input: this.handleInputChange,
      keydown: this.handleInputKeydown,
      // 轮子事件
      wheel: this.handleWheel,
    }

    const data = {
      style: style,
      class: css,
      attrs: attrs,
      props: props,
      on: events,
      ref: 'input',
    }

    if (this.buttonPosition === 'right') {
      return (
        <Input
          {...data}
        >
          {this.renderButtons()}
        </Input>
      )
    }

    const minusButton = this.renderButton()
    const plusButton = this.renderButton(true)

    return (
      <Input
        {...data}
      >
        {minusButton}
        {plusButton}
      </Input>
    )
  },
}
